import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Currency,
  DateTime,
  DeleteConfirmation,
  Icon,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Page,
  Tooltip,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useAuth, useRowSelection } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import TitleTooltip from '../../TitleTooltip';
import AddMultipleDialog from './AddMultipleDialog';
import BulkDelete from './BulkDelete';
import OtherItemForm from './OtherItemForm';
import SettingsActionsBanner from '../../SettingsActionsBanner';

const InvoicedIcon = styled(Icon)`
  color: ${colors.primary};
`;

export default function OtherItems({ project }) {
  const auth = useAuth();
  const canEdit = project.permissions.edit && auth.projects.sections.billing;
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).otherItems().get();
    setData(data);
  }, [api, workspace.id, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selection = useRowSelection({ rows: data });

  const [dialog, setDialog] = useState(null);
  const toast = useToast();

  const handleSaved = async () => {
    await fetchData();
    setDialog(null);
  };

  const handleShowForm = (otherItem = null) => {
    setDialog(
      <OtherItemForm
        currency={project.currency}
        projectId={project.id}
        otherItemId={otherItem?.id}
        readOnly={!canEdit}
        onSaved={handleSaved}
        onClose={() => setDialog(null)}
      />,
    );
  };

  const handleShowMultipleForm = () => {
    setDialog(
      <AddMultipleDialog
        currency={project.currency}
        project={project}
        onSaved={handleSaved}
        onCancel={() => setDialog(null)}
      />,
    );
  };

  const confirmation = useConfirmation();
  const handleDelete = async (otherItem) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        title="Delete Other Item"
        resolve={async (result) => {
          if (result) {
            await api.www.workspaces(workspace.id).projectSettings(project.id).otherItems(otherItem.id).delete();
            toast.success('Other Item deleted successfully.');
            await fetchData();
            selection.deselect(otherItem.id);
          }
          resolve(result);
        }}>
        Are you sure you want to delete this other item?
      </DeleteConfirmation>
    ));
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        projectId={project.id}
        ids={selection.ids}
        currency={project.currency}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  if (!data) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Other Items</Page.Title>
        <TitleTooltip message="Schedule the invoicing of other items. These could include miscellaneous items outside of traditional products and services." />

        {canEdit && (
          <Page.Actions>
            <IconButton icon="clone-plus" tooltip="Add Multiple" onClick={handleShowMultipleForm} />

            <IconButton icon="plus" tooltip="Add" onClick={() => handleShowForm(null)} />
          </Page.Actions>
        )}
      </Page.TabHeader>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListViewSelection.Column
              testId="other_items_bulk_checkbox"
              selection={selection}
              isVisible={canEdit}
              disabled={!data.length}
            />
            <ListView.Column width="8rem">Date</ListView.Column>
            <ListView.Column minWidth="10rem">Invoice Item</ListView.Column>
            <ListView.Column minWidth="20rem">Notes</ListView.Column>
            <ListView.Column width="10rem" align="right">
              Amount
            </ListView.Column>
            <ListView.Column width="3rem" />
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body>
            {data.map((otherItem) => {
              const isInvoiced = otherItem.invoice && otherItem.invoice.statusId !== 'draft';
              const areActionsDisabled = isInvoiced;
              const tooltip = isInvoiced ? "Invoiced other items can't be modified." : undefined;

              return (
                <ListView.Row
                  key={otherItem.id}
                  onClick={canEdit && areActionsDisabled ? undefined : () => handleShowForm(otherItem)}>
                  <ListViewSelection.Cell id={otherItem.id} selection={selection} testId="checkbox_other_item" />
                  <ListView.Cell>
                    <DateTime value={otherItem.date} />
                  </ListView.Cell>
                  <ListView.Cell>{otherItem.invoiceItem.name}</ListView.Cell>
                  <ListView.Cell>{otherItem.description}</ListView.Cell>
                  <ListView.Cell>
                    <Currency value={otherItem.fee} currency={otherItem.project.currency} />
                  </ListView.Cell>
                  <ListView.Cell>
                    {isInvoiced && (
                      <Tooltip message="An invoice has been published for this other item.">
                        <InvoicedIcon icon="file-invoice-dollar" />
                      </Tooltip>
                    )}
                  </ListView.Cell>
                  <ListViewActions>
                    {canEdit ? (
                      <>
                        <ListViewActions.Edit
                          disabled={areActionsDisabled}
                          tooltip={tooltip}
                          onClick={() => handleShowForm(otherItem)}
                        />
                        <hr />
                        <ListViewMenu>
                          <ListViewMenu.Item disabled={areActionsDisabled} onClick={() => handleShowForm(otherItem)}>
                            Edit
                          </ListViewMenu.Item>

                          <ListViewMenu.DeleteItem
                            disabled={areActionsDisabled}
                            tooltip={tooltip}
                            onClick={() => handleDelete(otherItem)}>
                            Delete
                          </ListViewMenu.DeleteItem>
                        </ListViewMenu>
                      </>
                    ) : (
                      <ListViewActions.View onClick={() => handleShowForm(otherItem)} />
                    )}
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length > 0 && (
              <ListView.TotalRow>
                <ListView.Cell />
                <ListView.Cell>Total</ListView.Cell>
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell>
                  <Currency value={_.sumBy(data, 'fee')} currency={project.currency} />
                </ListView.Cell>
                <ListView.Cell />
                <ListViewActions.Cell />
              </ListView.TotalRow>
            )}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data.length} label="Other Item" />
        </ListView>
      </Page.ListView>

      {dialog}

      {selection.any && (
        <SettingsActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <BulkActionMenu.Item
                onClick={() => {
                  setIsOpen(false);
                  handleBulkDelete();
                }}>
                Delete
              </BulkActionMenu.Item>
            )}
          </BulkActionMenu>
        </SettingsActionsBanner>
      )}
    </>
  );
}
